import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Receipt, Users, SplitSquareHorizontal, Trash2, ChevronRight,
  ScanLine, Moon, Sun, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/lib/theme";
import { createBill } from "@/lib/api";

const RECENTS_KEY = "gz-recent-bills";

export function loadRecents() {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
  } catch {
    return [];
  }
}
export function saveRecent(bill) {
  const list = loadRecents().filter((b) => b.id !== bill.id);
  list.unshift({ id: bill.id, title: bill.title, at: Date.now() });
  localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, 12)));
}
function removeRecent(id) {
  localStorage.setItem(RECENTS_KEY, JSON.stringify(loadRecents().filter((b) => b.id !== id)));
}

const steps = [
  { icon: ScanLine, label: "Scan the receipt", desc: "gigazip reads every line item and detects the currency." },
  { icon: Users, label: "Add your people", desc: "Colour-coded avatars, duplicate names handled automatically." },
  { icon: SplitSquareHorizontal, label: "Tap to assign", desc: "Share a dish or split it in fractions with a tap." },
  { icon: Receipt, label: "Exact totals", desc: "Service, tax, discounts and fair rounding, to the cent." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [recents, setRecents] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => setRecents(loadRecents()), []);

  const start = async () => {
    setBusy(true);
    try {
      const bill = await createBill("New bill");
      saveRecent(bill);
      navigate(`/bill/${bill.id}`);
    } catch (e) {
      toast.error("Could not start a bill. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--gz-header)]">
      {/* ---------- BLUE HEADER ---------- */}
      <div className="max-w-[440px] w-full mx-auto px-[18px] gz-safe-top pb-8 text-white">
        <div className="flex justify-end">
          <button
            data-testid="theme-toggle-btn"
            onClick={toggle}
            className="grid place-items-center h-[31px] w-[31px] rounded-[10px] bg-white/[0.13] hover:bg-white/25 active:scale-90 transition"
            aria-label="Toggle dark mode"
          >
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </div>
        <div className="flex justify-center pt-2 pb-4 gz-rise">
          <img
            src="/gigazip-logo.webp"
            alt="gigazip splitbill"
            className="w-[86%] max-w-[320px] select-none pointer-events-none"
            draggable="false"
          />
        </div>
      </div>

      {/* ---------- WHITE SHEET ---------- */}
      <div className="flex-1 bg-[var(--gz-body)] rounded-t-[22px]">
        <div className="max-w-[440px] w-full mx-auto px-[18px] pt-5 pb-8">
          {/* CTA */}
          <button
            data-testid="start-bill-btn"
            onClick={start}
            disabled={busy}
            className="w-full min-h-[48px] rounded-[12px] bg-[var(--gz-primary)] hover:opacity-95 text-white text-[13px] font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition disabled:opacity-40"
          >
            <Plus size={17} strokeWidth={1.9} />
            {busy ? "Starting…" : "Start a new bill"}
          </button>
          <p className="text-center text-[11px] text-[var(--gz-muted)] mt-2.5">
            No sign-in needed · Currency auto-detected
          </p>

          {/* How it works */}
          <div className="mt-6">
            <span className="block text-[11px] font-medium text-[var(--gz-muted)] mb-2">How it works</span>
            <div className="space-y-2">
              {steps.map((s, i) => (
                <div
                  key={s.label}
                  className="flex items-center gap-3 rounded-[14px] border border-[var(--gz-line)] bg-[var(--gz-card)] p-3 gz-rise"
                  style={{ animationDelay: `${80 + i * 55}ms` }}
                >
                  <div className="grid place-items-center h-9 w-9 shrink-0 rounded-[11px] bg-[var(--gz-primary)]/10 text-[var(--gz-primary)]">
                    <s.icon size={17} strokeWidth={1.7} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold">{s.label}</div>
                    <div className="text-[11.5px] text-[var(--gz-muted)] leading-snug mt-0.5">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent bills */}
          <div className="mt-6">
            <span className="block text-[11px] font-medium text-[var(--gz-muted)] mb-2">Recent bills</span>
            {recents.length > 0 ? (
              <div className="rounded-[14px] border border-[var(--gz-line)] bg-[var(--gz-card)] overflow-hidden">
                {recents.map((b) => (
                  <div key={b.id} className="flex items-center gap-2 px-3 py-2.5 border-b last:border-b-0 border-[var(--gz-line)]">
                    <button
                      data-testid={`recent-bill-${b.id}`}
                      onClick={() => navigate(`/bill/${b.id}`)}
                      className="flex-1 text-left min-w-0"
                    >
                      <div className="text-[13px] font-semibold truncate">{b.title || "Untitled bill"}</div>
                      <div className="text-[11px] text-[var(--gz-muted)]">
                        {new Date(b.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        removeRecent(b.id);
                        setRecents(loadRecents());
                      }}
                      aria-label="Remove"
                      className="grid place-items-center h-9 w-9 rounded-full text-[var(--gz-muted)] hover:text-[var(--gz-primary)] active:scale-90 transition"
                    >
                      <Trash2 size={15} strokeWidth={1.8} />
                    </button>
                    <ChevronRight size={15} strokeWidth={1.8} className="text-[var(--gz-muted)]" />
                  </div>
                ))}
              </div>
            ) : (
              <button
                onClick={start}
                className="w-full rounded-[14px] border border-dashed border-[var(--gz-line)] bg-[var(--gz-card)] px-4 py-7 text-center active:scale-[0.99] transition"
              >
                <div className="grid place-items-center h-10 w-10 mx-auto rounded-[11px] bg-[var(--gz-primary)]/10 text-[var(--gz-primary)] mb-2">
                  <Receipt size={18} strokeWidth={1.7} />
                </div>
                <p className="text-[13px] font-semibold">No bills yet</p>
                <p className="text-[11.5px] text-[var(--gz-muted)] mt-0.5 inline-flex items-center gap-1">
                  Start your first split <ArrowRight size={12} strokeWidth={1.9} />
                </p>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
